// A single "the visitor has engaged" signal for third-party analytics.
//
// Meta Pixel and PostHog used to start once the page had loaded and gone idle
// — still inside the first seconds, where their long tasks (~100 ms each for
// Meta's config, posthog-js and its session recorder) compete with the page
// settling. They now start on the visitor's first scroll, wheel, pointer or
// key press, or FALLBACK_MS after `load` for someone who only reads. Events
// fired before that are queued (see TrackingPixels' fbq stub and
// posthog-capture's queue), not lost.

const EVENTS = ["scroll", "wheel", "pointerdown", "touchstart", "keydown"];
export const FALLBACK_MS = 5000;

let engaged: Promise<void> | undefined;

export function whenEngaged(): Promise<void> {
  if (typeof window === "undefined") return new Promise(() => {});
  engaged ??= new Promise<void>((resolve) => {
    let timer: number | undefined;
    const go = () => {
      for (const e of EVENTS) window.removeEventListener(e, go, true);
      window.clearTimeout(timer);
      resolve();
    };
    for (const e of EVENTS) {
      window.addEventListener(e, go, { capture: true, passive: true });
    }
    const arm = () => {
      timer = window.setTimeout(go, FALLBACK_MS);
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
  });
  return engaged;
}

/** Run `fn` once engaged, in the next idle period so it stays off the frame
 *  that handles the interaction itself. */
export function afterEngagement(fn: () => void): void {
  void whenEngaged().then(() => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      window.setTimeout(fn, 0);
    }
  });
}
